// Pure-geometry rigging warnings. No mechanical-advantage / friction model is
// involved — these are direction-only relationships that hold for any line
// tension, so they are safe to show without a force engine.

export type Severity = 'ok' | 'caution' | 'danger'

/**
 * Resultant force on a redirect (deviation / pulley) as a multiple of the line
 * tension, assuming a frictionless redirect with equal tension in both legs.
 *
 * `deviationDeg` is how far the rope is turned away from straight-through
 * (0deg = straight, 180deg = fully reversed). The resultant on the anchor is
 * 2*T*sin(deviation/2), i.e. a pure ratio independent of the actual tension.
 */
export function redirectLoadFactor(deviationDeg: number): number {
  const rad = (Math.max(0, Math.min(180, deviationDeg)) * Math.PI) / 180
  return 2 * Math.sin(rad / 2)
}

/**
 * Severity keyed off the resultant load multiple:
 *   < 1.0x   ok       (deviation < 60deg)  — anchor sees less than line tension
 *   1.0-1.41 caution  (60-90deg)           — anchor sees 1.0-1.4x line tension
 *   >= 1.41  danger   (>= 90deg)           — anchor sees >40% over line tension
 */
export function loadSeverity(loadFactor: number): Severity {
  if (loadFactor >= 1.414) return 'danger'
  if (loadFactor >= 1.0) return 'caution'
  return 'ok'
}

export function deviationSeverity(deviationDeg: number): Severity {
  return loadSeverity(redirectLoadFactor(deviationDeg))
}

export const SEVERITY_COLOR: Record<Severity, string> = {
  ok: '#0f766e',
  caution: '#b45309',
  danger: '#dc2626',
}

export function formatLoadFactor(loadFactor: number): string {
  return `${loadFactor < 10 ? loadFactor.toFixed(1) : Math.round(loadFactor)}\u00d7T`
}
